import { FC, useEffect, useState } from 'react';
import {
  SectionStateType,
  TabContextState,
  TabSections,
  TabSectionType,
} from 'modules/iterableGoals/types';
import { TAB_ACTIONS, useRumAction } from 'services/observability/datadog';
import { TabContext } from './TabContext';
import { tabSectionMap } from '../constants';
import { useQueryParamContext } from './QueryParamContext';

export const TabContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { tab: selectedTab, setTab } = useQueryParamContext();
  const addSignal = useRumAction(TAB_ACTIONS);
  const [sections, setSections] = useState<TabSectionType<TabSections>>(tabSectionMap[selectedTab]);

  useEffect(() => {
    addSignal('TAB_CHANGED', {
      selectedTab,
    });
  }, [selectedTab]);

  const updateSectionState = (
    key: TabSections,
    value: boolean,
    sectionState: keyof SectionStateType,
  ) => setSections((state) => ({ ...state, [key]: { ...state[key], [sectionState]: value } }));

  const context: TabContextState = {
    selectedTab,
    setSelectedTab: (tab) => {
      if (tab === selectedTab) return;
      setTab(tab);
      setSections(tabSectionMap[tab]);
    },
    sections,
    setErrorState: (key: TabSections, value: boolean) => updateSectionState(key, value, 'error'),
    setLoadingState: (key: TabSections, value: boolean) =>
      updateSectionState(key, value, 'loading'),
    /**
     * By default all init tab sections are set to loading state.
     * So, we just have to reset to the default state to set all sections to loading.
     */
    loadAllSections: () => setSections(tabSectionMap[selectedTab]),
  };

  return <TabContext.Provider value={context}>{children}</TabContext.Provider>;
};
